import moment from 'moment';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { SchemaOf } from 'yup';
import { useMemo } from 'react';

import { useCurrentUser } from '@/react/hooks/useUser';
import { pluralize } from '@/react/common/string-utils';
import { silenceAlertFormDataToPayload } from '@/react/portainer/observability/AlertsView/ActiveAlertsDatatable/utils';

import { Button, LoadingButton } from '@@/buttons';
import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';
import { TextArea } from '@@/form-components/Input/Textarea';
import { Modal } from '@@/modals';
import { Badge } from '@@/Badge';
import { PortainerSelect } from '@@/form-components/PortainerSelect';

import { ActiveAlertRow } from './types';
import { useCreateSilence } from './useCreateSilence';
import { SilenceFormData } from './silenceTypes';

type Duration = '4h' | '8h' | '1d' | '5d' | 'custom';

const durationOptions = [
  { value: '4h', label: '4 hours' },
  { value: '8h', label: '8 hours' },
  { value: '1d', label: '1 day' },
  { value: '5d', label: '5 days' },
  { value: 'custom', label: 'Custom' },
] as const;

const durationAddArgs: Record<
  Exclude<Duration, 'custom'>,
  [number, moment.unitOfTime.DurationConstructor]
> = {
  '4h': [4, 'hours'],
  '8h': [8, 'hours'],
  '1d': [1, 'day'],
  '5d': [5, 'days'],
};

const DATE_FORMAT = 'YYYY-MM-DDTHH:mm';

const validationSchema: SchemaOf<SilenceFormValues> = yup.object({
  comment: yup.string().required('Comment is required'),
  createdBy: yup.string().required('Created by is required'),
  duration: yup.mixed<Duration>().required('Duration is required'),
  startsAt: yup.string().required('Start time is required'),
  endsAt: yup.string().required('End time is required'),
});

type SilenceFormValues = {
  comment: string;
  createdBy: string;
  duration: Duration;
  startsAt: string;
  endsAt: string;
};

interface Props {
  alerts: ActiveAlertRow[];
  onSave: () => void;
  onCancel: () => void;
  baseStartTime?: Date;
}

export function SilenceAlertForm({
  alerts,
  onSave,
  onCancel,
  baseStartTime = new Date(Date.now() + 60 * 1000),
}: Props) {
  const { user } = useCurrentUser();
  const createSilenceMutation = useCreateSilence();

  const matchers: SilenceFormData['matchers'] = useMemo(
    () =>
      alerts.map((alert) => [
        {
          name: 'alertname',
          value: alert.alertName,
          isRegex: false,
          isEqual: true,
        },
        {
          name: 'alert_rule_id',
          value: alert.alertRuleId,
          isRegex: false,
          isEqual: true,
        },
        {
          name: 'alert_source',
          value: alert.labels.alert_source ?? alert.source,
          isRegex: false,
          isEqual: true,
        },
        ...(alert.labels.portainer_environment_id
          ? [
              {
                name: 'portainer_environment_id',
                value: alert.labels.portainer_environment_id,
                isRegex: false,
                isEqual: true,
              },
            ]
          : []),
        ...(alert.labels.portainer_environment_group_id
          ? [
              {
                name: 'portainer_environment_group_id',
                value: alert.labels.portainer_environment_group_id,
                isRegex: false,
                isEqual: true,
              },
            ]
          : []),
        ...(alert.environmentName
          ? [
              {
                name: 'portainer_environment_name',
                value: alert.environmentName,
                isRegex: false,
                isEqual: true,
              },
            ]
          : []),
        ...(alert.environmentGroupName
          ? [
              {
                name: 'portainer_environment_group_name',
                value: alert.environmentGroupName,
                isRegex: false,
                isEqual: true,
              },
            ]
          : []),
      ]),
    [alerts]
  );
  const initialValues: SilenceFormValues = useMemo(
    () => ({
      comment: `Silencing ${pluralize(alerts.length, 'alert')}: ${alerts
        .map((a) => a.alertName)
        .join(', ')}`,
      createdBy: user?.Username || '',
      duration: '4h',
      startsAt: moment(baseStartTime).format(DATE_FORMAT),
      endsAt: moment(baseStartTime)
        .add(...durationAddArgs['4h'])
        .format(DATE_FORMAT),
    }),
    [alerts, baseStartTime, user?.Username]
  );

  async function handleSubmit(values: SilenceFormValues) {
    const payloads = silenceAlertFormDataToPayload({
      comment: values.comment,
      createdBy: values.createdBy,
      startsAt: values.startsAt,
      endsAt: values.endsAt,
      matchers,
    });

    await createSilenceMutation.mutateAsync({
      silenceData: payloads,
      // TODO: Get the actual alert manager URL from settings
      // For now, using the default internal AlertManager
      alertManagerURL: 'localhost:9093',
    });
    onSave();
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
        submitForm,
      }) => {
        const isCustom = values.duration === 'custom';
        const isLoading = createSilenceMutation.isLoading;

        function handleDurationChange(newDuration: Duration) {
          setFieldValue('duration', newDuration);
          if (newDuration !== 'custom') {
            const [amount, unit] =
              durationAddArgs[newDuration as Exclude<Duration, 'custom'>];
            setFieldValue(
              'startsAt',
              moment(baseStartTime).format(DATE_FORMAT)
            );
            setFieldValue(
              'endsAt',
              moment(baseStartTime).add(amount, unit).format(DATE_FORMAT)
            );
          }
        }

        return (
          <>
            <Modal.Header title="Silence Alert" />

            <Modal.Body>
              <Form className="form-horizontal">
                <FormControl
                  label="Comment"
                  required
                  inputId="comment"
                  errors={
                    errors.comment && touched.comment
                      ? errors.comment
                      : undefined
                  }
                >
                  <TextArea
                    id="comment"
                    name="comment"
                    value={values.comment}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Reason for silencing this alert..."
                    required
                    rows={3}
                    data-cy="silence-comment-input"
                    disabled={isLoading}
                  />
                </FormControl>

                <FormControl
                  label="Created By"
                  required
                  inputId="createdBy"
                  errors={
                    errors.createdBy && touched.createdBy
                      ? errors.createdBy
                      : undefined
                  }
                >
                  <Input
                    id="createdBy"
                    name="createdBy"
                    value={values.createdBy}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Your name or username"
                    required
                    data-cy="silence-created-by-input"
                    disabled={isLoading}
                  />
                </FormControl>

                <FormControl
                  label="Duration"
                  required
                  inputId="duration"
                  errors={
                    errors.duration && touched.duration
                      ? errors.duration
                      : undefined
                  }
                >
                  <PortainerSelect
                    name="duration"
                    value={values.duration}
                    onChange={handleDurationChange}
                    onBlur={handleBlur}
                    options={durationOptions}
                    data-cy="silence-duration-select"
                    disabled={isLoading}
                  />
                </FormControl>

                <FormControl
                  label="Starts At"
                  required
                  inputId="startsAt"
                  errors={
                    errors.startsAt && touched.startsAt
                      ? errors.startsAt
                      : undefined
                  }
                >
                  <Input
                    id="startsAt"
                    name="startsAt"
                    type="datetime-local"
                    value={values.startsAt}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    data-cy="silence-starts-at-input"
                    disabled={isLoading || !isCustom}
                    readOnly={!isCustom}
                  />
                </FormControl>

                <FormControl
                  label="Ends At"
                  required
                  inputId="endsAt"
                  errors={
                    errors.endsAt && touched.endsAt ? errors.endsAt : undefined
                  }
                >
                  <Input
                    id="endsAt"
                    name="endsAt"
                    type="datetime-local"
                    value={values.endsAt}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    data-cy="silence-ends-at-input"
                    disabled={isLoading || !isCustom}
                    readOnly={!isCustom}
                  />
                </FormControl>

                {/* Matchers Information (Read-only) */}
                <div className="form-group">
                  <label
                    className="col-sm-3 col-lg-2 control-label text-left"
                    htmlFor="matchers-info"
                  >
                    Matchers
                  </label>
                  <div className="col-sm-9 col-lg-10">
                    <div id="matchers-info" className="form-control-static">
                      <p className="text-muted small mb-3">
                        This silence will match alerts with the following
                        criteria:
                      </p>
                      {matchers.map((alertMatcherGroup, idx) => (
                        <div key={idx} className="mb-6">
                          {alertMatcherGroup.map((matcher, index) => (
                            <div key={index} className="mb-2">
                              <Badge type="info" className="mr-2">
                                {matcher.name}
                              </Badge>
                              <span className="text-muted">equals</span>
                              <Badge type="muted" className="ml-2">
                                {matcher.value}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Form>
            </Modal.Body>

            <Modal.Footer>
              <Button
                type="button"
                color="default"
                onClick={onCancel}
                data-cy="silence-cancel-button"
                disabled={isLoading}
              >
                Cancel
              </Button>
              <LoadingButton
                type="button"
                color="primary"
                onClick={submitForm}
                data-cy="silence-submit-button"
                isLoading={isLoading}
                loadingText="Creating..."
              >
                Create Silence
              </LoadingButton>
            </Modal.Footer>
          </>
        );
      }}
    </Formik>
  );
}
